import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, Keyboard, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/common";
import { api, apiErr } from "@/lib/api";

export default function Escanear() {
  const nav = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [loading, setLoading] = useState(false);
  const qrRef = useRef(null);
  const scannerRef = useRef(null);

  const resolve = async (token) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/scan/${token}`);
      const eq = data.equipment;
      nav("/app/registro", { state: { equipment: {
        id: eq.id, name: eq.name, min_temperature: data.min_temperature,
        max_temperature: data.max_temperature, unit: data.unit,
        location_name: data.location_name, area_name: data.area_name,
      }}});
    } catch (e) {
      toast.error(apiErr(e, "Código QR no reconocido"));
    } finally { setLoading(false); }
  };

  const startScan = async () => {
    setScanning(true);
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
        async (decoded) => { await scanner.stop(); setScanning(false); resolve(decoded); },
        () => {});
    } catch {
      setScanning(false);
      toast.error("No se pudo iniciar la cámara. Ingresa el código manualmente.");
    }
  };

  useEffect(() => () => { scannerRef.current?.stop().catch(() => {}); }, []);

  return (
    <div className="px-4 pt-5">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Escanear QR</h1>
      <p className="mt-1 text-sm text-slate-500">Apunta al código del equipo para iniciar el registro.</p>

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-10 text-slate-500"><Loader2 className="h-8 w-8 animate-spin text-primary" /> Verificando equipo…</div>
        ) : scanning ? (
          <div id="qr-reader" ref={qrRef} className="w-full overflow-hidden rounded-xl" />
        ) : (
          <EmptyState icon={QrCode} title="Listo para escanear"
            subtitle="Usa la cámara o ingresa el código del equipo."
            action={<Button data-testid="start-scan-btn" onClick={startScan} className="h-12 rounded-xl"><Camera className="mr-2 h-5 w-5" /> Iniciar cámara</Button>} />
        )}
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-600"><Keyboard className="h-4 w-4" /> Ingresar código manualmente</p>
        <div className="flex gap-2">
          <Input data-testid="manual-qr-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Código del equipo" className="h-12" />
          <Button data-testid="manual-qr-btn" disabled={!manual} onClick={() => resolve(manual.trim())} className="h-12 rounded-xl">Ir</Button>
        </div>
      </div>
    </div>
  );
}
